import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { API_BASE } from '../config';
import { useAuth } from './AuthContext';
import { useLocale } from './LocaleContext';
import type { OnboardingProgress } from '../types/onboarding';
import {
  ONBOARDING_UPDATE_EVENT,
  type OnboardingUpdatePayload,
} from '../onboarding/notify-onboarding-update';
import {
  ChapterZeroAchievementToast,
  type ChapterZeroToastItem,
} from '../components/ChapterZeroAchievementToast';
import { OnboardingMobileBubble } from '../components/OnboardingMobileBubble';

type OnboardingContextValue = {
  progress: OnboardingProgress | null;
  loading: boolean;
  celebrationKey: number;
  refresh: () => Promise<void>;
  mobileBubbleOpen: boolean;
  setMobileBubbleOpen: (open: boolean) => void;
  registerMobileSidebarCloser: (closer: (() => void) | null) => void;
  openMobileBubble: () => void;
};

const emptyProgress: OnboardingProgress = {
  points: 0,
  finalMilestonePoints: 0,
  nextMilestonePoints: null,
  actions: [],
  milestones: [],
};

const OnboardingContext = createContext<OnboardingContextValue | undefined>(undefined);

function rewardToastItem(milestoneId: string, t: (key: string) => string): ChapterZeroToastItem {
  return {
    kind: 'reward',
    text: t('onboarding_toast_reward').replace(
      '{reward}',
      t(`onboarding_milestone_${milestoneId}`)
    ),
  };
}

function achievementToastItem(
  actionId: string,
  points: number,
  t: (key: string) => string
): ChapterZeroToastItem {
  return {
    kind: 'achievement',
    text: t('onboarding_toast_achievement')
      .replace('{achievement}', t(`onboarding_action_${actionId}`))
      .replace('{points}', String(points)),
  };
}

function buildCelebrationItems(
  previous: OnboardingProgress | null,
  next: OnboardingProgress,
  t: (key: string) => string
): ChapterZeroToastItem[] {
  if (!previous) {
    return [];
  }

  const items: ChapterZeroToastItem[] = [];
  for (const action of next.actions) {
    if (!action.completed) {
      continue;
    }
    const wasCompleted = previous.actions.find((item) => item.id === action.id)?.completed === true;
    if (wasCompleted) {
      continue;
    }
    items.push(achievementToastItem(action.id, action.points, t));
  }
  for (const milestone of next.milestones) {
    if (!milestone.granted) {
      continue;
    }
    const wasGranted =
      previous.milestones.find((item) => item.id === milestone.id)?.granted === true;
    if (wasGranted) {
      continue;
    }
    items.push(rewardToastItem(milestone.id, t));
  }
  return items;
}

export const OnboardingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { token, user } = useAuth();
  const { t } = useLocale();
  const [progress, setProgress] = useState<OnboardingProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [celebrationKey, setCelebrationKey] = useState(0);
  const [toastItems, setToastItems] = useState<ChapterZeroToastItem[] | null>(null);
  const [mobileBubbleOpen, setMobileBubbleOpen] = useState(false);
  const progressRef = useRef<OnboardingProgress | null>(null);
  const mobileSidebarCloserRef = useRef<(() => void) | null>(null);

  const registerMobileSidebarCloser = useCallback((closer: (() => void) | null): void => {
    mobileSidebarCloserRef.current = closer;
  }, []);

  const openMobileBubble = useCallback((): void => {
    mobileSidebarCloserRef.current?.();
    setMobileBubbleOpen(true);
  }, []);

  useEffect(() => {
    progressRef.current = progress;
  }, [progress]);

  const celebrateItems = useCallback((items: ChapterZeroToastItem[]): void => {
    if (items.length === 0) {
      return;
    }
    setCelebrationKey((key) => key + 1);
    window.setTimeout(() => {
      setToastItems(items);
    }, 400);
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    if (!token || !user) {
      setProgress(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/onboarding`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch onboarding progress');
      }
      const data = (await response.json()) as {
        progress: OnboardingProgress;
        newlyGrantedMilestones?: string[];
      };
      const nextProgress = data.progress ?? emptyProgress;
      const comparisonItems = buildCelebrationItems(progressRef.current, nextProgress, t);
      const catchUpItems = (data.newlyGrantedMilestones ?? []).map((milestoneId) =>
        rewardToastItem(milestoneId, t)
      );
      setProgress(nextProgress);
      celebrateItems(comparisonItems.length > 0 ? comparisonItems : catchUpItems);
    } catch (error) {
      console.error('Error fetching onboarding progress:', error);
    } finally {
      setLoading(false);
    }
  }, [token, user, t, celebrateItems]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const handleFocus = (): void => {
      void refresh();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [refresh]);

  useEffect(() => {
    const handleUpdate = (event: Event): void => {
      const customEvent = event as CustomEvent<OnboardingUpdatePayload>;
      const payload = customEvent.detail;
      if (!payload) {
        return;
      }

      setProgress(payload.progress);

      const items: ChapterZeroToastItem[] = [];
      if (payload.newlyCompleted) {
        items.push(achievementToastItem(payload.actionId, payload.pointsAwarded, t));
      }
      for (const milestoneId of payload.newlyGrantedMilestones) {
        items.push(rewardToastItem(milestoneId, t));
      }

      celebrateItems(items);
    };

    window.addEventListener(ONBOARDING_UPDATE_EVENT, handleUpdate);
    return () => window.removeEventListener(ONBOARDING_UPDATE_EVENT, handleUpdate);
  }, [t, celebrateItems]);

  useEffect(() => {
    if (!toastItems) {
      return;
    }
    const hideTimer = window.setTimeout(() => setToastItems(null), 7000);
    return () => window.clearTimeout(hideTimer);
  }, [toastItems]);

  const value = useMemo(
    () => ({
      progress,
      loading,
      celebrationKey,
      refresh,
      mobileBubbleOpen,
      setMobileBubbleOpen,
      registerMobileSidebarCloser,
      openMobileBubble,
    }),
    [
      progress,
      loading,
      celebrationKey,
      refresh,
      mobileBubbleOpen,
      registerMobileSidebarCloser,
      openMobileBubble,
    ]
  );

  return (
    <OnboardingContext.Provider value={value}>
      {children}
      <OnboardingMobileBubble />
      {toastItems && toastItems.length > 0 && (
        <ChapterZeroAchievementToast
          items={toastItems}
          onClose={() => setToastItems(null)}
        />
      )}
    </OnboardingContext.Provider>
  );
};

export function useOnboarding(): OnboardingContextValue {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return context;
}
